import { Component, ElementRef, Input, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { Form, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { BaseComponent } from '../../../common/components/base.component';
import { localStorageKeys } from '../../../common/constants/local.storage.keys';
import { CustomDefaultPlaceCategory, VisitorSettingsCommand } from '../../../commands/pagination.query.command';
import { AlertifyService } from '../../../services/alertify.service';
import { ButtonTriggerEvent } from '../../../common/components';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { AccountApiService } from '../../../services/account.api.service';
import {
  CustomDefaultPlaceCategoryItemEditedStore,
  CustomDefaultPlaceCategoryStore,
} from '../../../stores/hub.calendar.store';
import { LocalStorageService } from '../../../services/localStorageService';
import { PlaceService } from '../../../services/place.service';
import { DropEvent } from 'ng-drag-drop';
import { guid } from '../../../utils/guid';
import { isEmptyObject, isNumber } from '../../../common/extensions';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'ig-hub-customize-place-category',
  templateUrl: './customize.place.category.component.html',
  styleUrls: ['./customize.place.category.component.scss'],
})
export class CustomizePlaceCategoryComponent extends BaseComponent implements OnInit {
  public hubName: string;
  public isUseDefault = true;
  public form: FormGroup;
  public igDefaultCategories: Map<string, string[]>;
  public mergedDefaultCategories: { keys: string[]; values: string[] }[];
  public clientPlaceCategories: PlaceType[] = [];
  public blockGooglePlaceCategories: string[] = [];
  public subCategoriesOfSelected = [];
  public selectedItem: PlaceType = null;
  public draggable = true;

  public editedCategories: EditedCategory[] = [];
  public duplicatedCategories: string[][] = [];

  @Input() disabled = false;
  @ViewChild('editor') editorArea: ElementRef;
  @ViewChild('categoryList') categoryListArea: ElementRef;
  @ViewChildren('accordionItem') accordions: QueryList<ElementRef>;

  constructor(
    private formBuilder: FormBuilder,
    private activatedRoute: ActivatedRoute,
    private accountApiService: AccountApiService,
    private alertify: AlertifyService,
    private customDefaultPlaceCategoryStore: CustomDefaultPlaceCategoryStore,
    private customDefaultPlaceCategoryItemEditedStore: CustomDefaultPlaceCategoryItemEditedStore
  ) {
    super({ activatedRoute });
  }

  public ngOnInit() {
    let { hubName } = this.allRouteParams;
    this.hubName = hubName;

    this.form = this.formBuilder.group({
      categoryName: [null, []],
    });

    this.loadCustomCategories();
    this.syncCategoryListAreaHeightWithEditorAreaHeight();

    this.customDefaultPlaceCategoryItemEditedStore.customDefaultPlaceCategoryItemEditedCommand
      .pipe(takeUntil(this.isDestroyed))
      .subscribe((data) => {
        if (isEmptyObject(data)) return;

        if (data.type === 'deleted') {
          this.setCategoryRemovedData(data.item);
        } else {
          this.onCategoryTitleEdited(data);
        }
      });
  }

  public get categoryLocator() {
    if (!this.selectedItem) return '';

    for (const firstLevel of this.clientPlaceCategories) {
      if (firstLevel.id === this.selectedItem.id) {
        return `<span class="primary-color-text">${firstLevel.title}</span>`;
      }

      for (let secondLevel of firstLevel.subCategories) {
        if (secondLevel.id === this.selectedItem.id) {
          return `<span>${firstLevel.title} > <span class="primary-color-text">${secondLevel.title}</span></span>`;
        }

        for (let thirdLevel of secondLevel.subCategories) {
          if (thirdLevel.id === this.selectedItem.id) {
            return `<span>${firstLevel.title} > ${secondLevel.title} > <span class="primary-color-text">${thirdLevel.title}</span></span>`;
          }
        }
      }
    }

    return '';
  }

  public changeUseDefault() {
    if (this.isUseDefault) {
      this.accountApiService.setUseDefaultPlaceCategory(this.hubName).subscribe(() => {
        this.alertify.successfully('Set to use default place categories');
        this.sendCategoryUpdateFlow();
        this.updateLocalStorage();
      });
    }
  }

  public expandCategory() {}

  public removeFirstLevel(item: PlaceType) {
    this.clientPlaceCategories = this.clientPlaceCategories.filter((x) => x.id !== item.id);
  }

  public onToggle(isOpened) {
    this.draggable = !isOpened;
    this.syncCategoryListAreaHeightWithEditorAreaHeight();
  }

  public syncCategoryListAreaHeightWithEditorAreaHeight() {
    setTimeout(() => {
      if (!this.editorArea || !this.categoryListArea) return;

      const rect = this.editorArea.nativeElement.getBoundingClientRect();
      this.categoryListArea.nativeElement.style.height = `${rect.height - 16}px`;
    });
  }

  public dragEnter($event) {
    if ($event && $event.currentTarget) {
      this.restoreItemStyle();
      $event.currentTarget.style.outline = '2px dashed green';
    }
  }

  private restoreItemStyle() {
    this.accordions.toArray().forEach((x) => {
      x.nativeElement.style.outline = 'none';
    });
  }

  public onItemDrop(e: DropEvent, targetIndex: number) {
    console.log({ e });
    console.log({ targetIndex });
    const dragItemIndex = this.clientPlaceCategories.indexOf(e.dragData as PlaceType);
    console.log({ dragItemIndex });

    // swap items
    [this.clientPlaceCategories[targetIndex], this.clientPlaceCategories[dragItemIndex]] = [
      this.clientPlaceCategories[dragItemIndex],
      this.clientPlaceCategories[targetIndex],
    ];

    setTimeout(() => {
      this.restoreItemStyle();
    });
  }

  public save(event: ButtonTriggerEvent) {
    let categoryTitles: string[] = [];
    for (let category of this.clientPlaceCategories) {
      category.isEditing = false;
      this.addSameNameCategory(category);

      PlaceService.getAllSubCategoriesWithEachLevelTitles(category, categoryTitles);
    }

    console.log('categoryTitles', categoryTitles);

    // check if any leaf level categories are duplicated
    let duplicatedCategories: string[][] = [];
    for (const title of categoryTitles) {
      const duplicated = categoryTitles.find(
        (x) => x.split(' > ').last().toLowerCase() === title.split(' > ').last().toLowerCase()
      );
      // const duplicated = categoryTitles.find((x) => x.split(' > ').any((y) => title.split(' > ').includes(y)));
      if (duplicated && title !== duplicated) {
        duplicatedCategories.push([title, duplicated]);
      }
    }

    // check if there is any duplicated not only leaf level level category
    const duplicates = categoryTitles.filter((item, index) => categoryTitles.indexOf(item) !== index);
    if (duplicates.length) {
      for (const d of duplicates) {
        duplicatedCategories.push([d]);
      }
    }

    console.log('duplicatedCategories', duplicatedCategories);
    if (duplicatedCategories.length) {
      this.duplicatedCategories = duplicatedCategories;
      event.immediateComplete();
      return;
    }
    this.duplicatedCategories = [];

    console.log(this.clientPlaceCategories);

    this.accountApiService
      .setCustomDefaultPlaceCategory(
        this.hubName,
        this.clientPlaceCategories,
        this.editedCategories.filter((x) => x.oldTitle !== x.newTitle)
      )
      .finally(event.complete)
      .subscribe(() => {
        this.alertify.successfully('Saving custom place categories');
        this.updateLocalStorage();
        this.sendCategoryUpdateFlow();
      });
  }

  /**
   * add a category code with the same name as category text so that user can select this category for a place
   * */
  private addSameNameCategory(category: PlaceType) {
    category.isEditing = false;
    const code = PlaceService.toCategoryCode(category.title);
    if (category.categoryList.indexOf(code) < 0) {
      category.categoryList.unshift(code);
    }

    if (category.subCategories && category.subCategories.length) {
      category.subCategories.forEach((x) => {
        x.isEditing = false;
        this.addSameNameCategory(x);
      });
    }
  }

  public loadCustomCategories() {
    setTimeout(() => {
      const customDefaultPlaceCategory = LocalStorageService.getCustomDefaultPlaceCategoryFromLocalStorage();

      this.isUseDefault = customDefaultPlaceCategory.useDefault;
      this.clientPlaceCategories = customDefaultPlaceCategory.customCategories;
      this.mergedDefaultCategories = customDefaultPlaceCategory.mergedDefaultCategories;

      this.blockGooglePlaceCategories = customDefaultPlaceCategory.blockGooglePlaceCategories
        ? customDefaultPlaceCategory.blockGooglePlaceCategories.split(', ')
        : [];

      this.syncCategoryListAreaHeightWithEditorAreaHeight();
    }, 2000);
  }

  public updateLocalStorage() {
    /** when searching places in the client dashboard sidebar, user can select to not using filters (search from all existing places)
         see getAllowedPlaceCategories in hub.calendar.component.ts
         this function will use this storage. So if the custom place category changes, clear the storage
         */
    localStorage.removeItem(localStorageKeys.allowedPlaceCategories);

    const allSettings = LocalStorageService.getAllBackendSettingsFromLocalStorage();
    if (!allSettings) return;

    allSettings.customSettings.customDefaultPlaceCategory.useDefault = this.isUseDefault;
    allSettings.customSettings.customDefaultPlaceCategory.customCategories = this.clientPlaceCategories;

    localStorage.setItem(localStorageKeys.allBackendSettings, JSON.stringify(allSettings));
  }

  public isSelected(categoryTitle: string) {
    const selected = this.isInSelectedCategory(categoryTitle);
    return selected;
  }

  public defaultCategorySelectionChange(checkbox: MatCheckboxChange) {
    const categoryCode = PlaceService.toCategoryCode(checkbox.source.name);
    if (checkbox.checked) {
      if (!this.isInSelectedCategory(categoryCode)) {
        this.selectedItem.categoryList.push(categoryCode);
      }
    } else {
      this.selectedItem.categoryList.splice(this.selectedItem.categoryList.indexOf(categoryCode), 1);
    }
  }

  public selectCategory(item: PlaceType) {
    console.log('custom', item);
    this.selectedItem = item;
    this.syncCategoryListAreaHeightWithEditorAreaHeight();
  }

  public onSubCategoryRemove($event, item: PlaceType) {
    this.selectedItem = null;
    this.removeFirstLevel(item);
    this.syncCategoryListAreaHeightWithEditorAreaHeight();
  }

  public setCategoryRemovedData(item: PlaceType) {
    /** after edited a category, need to notify the server side to update the existing places which hold this category */
    const exist = this.editedCategories.find((x) => x._id === item._id);
    if (exist) {
      exist.type = 'deleted';
      // has newTitle means this categories has been edited, should remove the newTitle so that server side can find and remove this category from the places
      exist.oldTitle = exist.newTitle;
      delete exist.newTitle;
    } else {
      this.editedCategories.push({
        _id: item._id,
        type: 'deleted',
        oldTitle: PlaceService.toCategoryCode(item.title),
      } as EditedCategory);
    }
  }

  public onCategoryTitleEdited($event: { item: PlaceType; oldTitle: string; newTitle: string }) {
    /** after edited a category, need to notify the server side to update the existing places which hold this category */
    const exist = this.editedCategories.find((x) => x._id === $event.item._id);
    if (exist) {
      exist.oldTitle = PlaceService.toCategoryCode($event.oldTitle);
      exist.newTitle = PlaceService.toCategoryCode($event.newTitle);
    } else {
      this.editedCategories.push({
        _id: $event.item._id,
        oldTitle: PlaceService.toCategoryCode($event.oldTitle),
        newTitle: PlaceService.toCategoryCode($event.newTitle),
        type: 'edited',
      } as EditedCategory);
    }
  }

  public add1stLevelCategory() {
    this.clientPlaceCategories.push(new PlaceType('New category', 0));
    this.syncCategoryListAreaHeightWithEditorAreaHeight();
  }

  public updateCategoryTitle($event: ButtonTriggerEvent) {}

  public showReadable(code: string) {
    return PlaceService.toReadableCategory(code);
  }

  private isInSelectedCategory(categoryTitle: string) {
    return this.selectedItem.categoryList.indexOf(categoryTitle) >= 0;
  }

  private sendCategoryUpdateFlow() {
    this.customDefaultPlaceCategoryStore.CustomDefaultPlaceCategoryCommand.next({
      useDefault: this.isUseDefault,
      customCategories: this.clientPlaceCategories,
    } as CustomDefaultPlaceCategory);

    /** when searching places in the client dashboard sidebar, user can select to not using filters (search from all existing places)
         see getAllowedPlaceCategories in hub.calendar.component.ts
         this function will use this storage. So if the custom place category changes, clear the storage
         */
    localStorage.removeItem(localStorageKeys.allowedPlaceCategories);
  }
}

export class PlaceType {
  _id: string;
  title: string;
  icon?: string;
  subCategories: PlaceType[];
  categoryList: string[];
  isEditing: boolean;
  id?: string;
  level: number | undefined;

  constructor(title?: string, level?: number) {
    this.title = title;
    this.icon = null;
    this.subCategories = [];
    this.categoryList = [];
    this.isEditing = false;
    this.id = guid();
    this.level = isNumber(level) ? level : undefined;
  }
}

export class EditedCategory {
  _id: string;
  type: 'deleted' | 'edited';
  oldTitle: string;
  newTitle: string;
}



// WEBPACK FOOTER //
// D:/Work/Development/ImGoing/IMGoing/web/src/components/hub/place.category/customize.place.category.component.ts